import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import css from './CTAButton.module.scss';
import { KioskCTAButtonDisplay } from './KioskCTAButtonDisplay/KioskCTAButtonDisplay';
import { WebCTAButtonDisplay } from './WebCTAButtonDisplay/WebCTAButtonDisplay';
import { BaseButtonBase } from '../BaseButtonBase/BaseButtonBase';
import { CTAButtonProps } from './CTAButton.types';
import { MouseEvent } from 'react';
import { isElectron } from '../../../helpers/EnvironmentFlags/EnvironmentFlags';

/**
 * The Call To Action button that is used for actions taken to progress the state of the form. Generally
 * used as a proceed button, search button, or some other variation that is at the bottom of the application.
 *
 * @param label - {@link CTAButtonProps.label label} description.
 * @param styleType - {@link CTAButtonProps.styleType styleType} description.
 * @param type - {@link CTAButtonProps.type type} description.
 * @param title - {@link CTAButtonProps.title title} description.
 * @param className - {@link CTAButtonProps.className className} description.
 * @param onClick - {@link CTAButtonProps.onClick onClick} description.
 * @param disabled - {@link CTAButtonProps.disabled disabled} description.
 * @param hideIcon - {@link CTAButtonProps.hideIcon hideIcon} description.
 * @param kioskOptions - {@link CTAButtonProps.kioskOptions kioskOptions} description.
 * @param webOptions - {@link CTAButtonProps.webOptions webOptions} description.
 * @param style - {@link CTAButtonProps.style style} description.
 * @param ariaLabel - {@link CTAButtonProps.ariaLabel ariaLabel} description.
 * @param id - {@link CTAButtonProps.id id} description.
 * @param displayDataTestid - {@link CTAButtonProps.displayDataTestid displayDataTestid} description.
 */
const CTAButton = ({
    label,
    styleType,
    type,
    title,
    className,
    onClick,
    disabled,
    hideIcon,
    kioskOptions,
    webOptions,
    style,
    ariaLabel,
    id,
    displayDataTestid,
}: CTAButtonProps): ReactNode => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isKiosk: boolean = isElectron();

    const handleOnClick = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
        if (onClick && !isSubmitting) {
            setIsSubmitting(true);

            try {
                await onClick(event);
            }
            finally {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <BaseButtonBase
            key={'ctaButton' + label + styleType}
            onClick={handleOnClick}
            type={type}
            title={title}
            disabled={isSubmitting || disabled}
            classes={{
                root: classNames(css.ctaButton, css[styleType], className),
            }}
            style={style}
            id={id}
            ariaLabel={ariaLabel}
        >
            {isKiosk ? (
                <KioskCTAButtonDisplay
                    {...kioskOptions}
                    label={label}
                    hideIcon={hideIcon}
                    dataTestid={displayDataTestid ? displayDataTestid + '-kiosk' : undefined}
                />
            ) : (
                <WebCTAButtonDisplay
                    {...webOptions}
                    label={label}
                    hideIcon={hideIcon}
                    dataTestid={displayDataTestid ? displayDataTestid + '-web' : undefined}
                />
            )}
        </BaseButtonBase>
    );
};

export default CTAButton;